import { useEffect, useRef, useState, useSyncExternalStore, type CSSProperties, type ReactNode } from 'react'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'

import { Button, type ZapButtonVariant } from './Button'
import { Fab } from './Fab'
import './PendingChangesDock.css'

export interface PendingChangesDockAction {
  key?: string
  label: string
  variant?: ZapButtonVariant
  onClick: () => void
  loading?: boolean
  disabled?: boolean
  icon?: ReactNode
  globalLoading?: { label: string; messages: string[] }
}

export interface PendingChangesDockProps {
  /** When true, the popup card is forced open regardless of hover/focus. */
  open?: boolean
  /** Icon rendered inside the resting FAB. Defaults to a save disk. */
  icon?: ReactNode
  /** Popup title — the main call to action. */
  title: string
  /** Optional secondary line under the title. */
  summary?: string
  /** Save-style actions rendered inside the popup, in order. */
  actions: PendingChangesDockAction[]
  /** When provided, a Discard button appears in the popup footer. */
  onDiscard?: () => void
  discardLabel?: string
  /** Additional class applied to the dock root — use for absolute placement. */
  className?: string
  /** Additional inline style applied to the dock root. */
  style?: CSSProperties
  /** Where the popup opens relative to the FAB. Defaults to top-end. */
  popupPlacement?: 'top-end' | 'top-start' | 'bottom-end' | 'bottom-start'
  /** ARIA label for the resting FAB. Defaults to the popup title. */
  ariaLabel?: string
  /** Pulse the FAB briefly on mount to draw attention. Defaults to true. */
  pulseOnMount?: boolean
}

const placementClass = (placement: PendingChangesDockProps['popupPlacement']): string => {
  switch (placement) {
    case 'top-start': return 'pendingChangesDock--top-start'
    case 'bottom-end': return 'pendingChangesDock--bottom-end'
    case 'bottom-start': return 'pendingChangesDock--bottom-start'
    case 'top-end':
    default:
      return 'pendingChangesDock--top-end'
  }
}

/**
 * PendingChangesDock — a compact save-changes surface.
 *
 * At rest it is a small circular FAB with an accent badge. Hovering or focusing
 * the FAB reveals a popup card with the summary and action buttons. Pass
 * `open` to force the popup open (e.g. while a draft is active).
 */
const emptySubscribe = () => () => {}

export function PendingChangesDock({
  open = false,
  icon,
  title,
  summary,
  actions,
  onDiscard,
  discardLabel = 'Discard',
  className,
  style,
  popupPlacement = 'top-end',
  ariaLabel,
  pulseOnMount = true,
}: PendingChangesDockProps) {
  const mounted = useSyncExternalStore(emptySubscribe, () => true, () => false)
  const [pulse, setPulse] = useState(pulseOnMount)
  const fabRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (!pulseOnMount) return
    const handle = window.setTimeout(() => setPulse(false), 2400)
    return () => window.clearTimeout(handle)
  }, [pulseOnMount])

  const rootClass = [
    'pendingChangesDock',
    placementClass(popupPlacement),
    open ? 'pendingChangesDock--open' : '',
    mounted ? 'pendingChangesDock--mounted' : '',
    pulse ? 'pendingChangesDock--pulse' : '',
    className ?? '',
  ].filter(Boolean).join(' ')

  return (
    <div className={rootClass} style={style} data-open={open || undefined}>
      <span className="pendingChangesDock__fabWrap">
        <Fab
          ref={fabRef}
          className="pendingChangesDock__fab"
          label={ariaLabel ?? title}
          aria-haspopup="dialog"
          aria-expanded={open || undefined}
          icon={
            <span className="pendingChangesDock__fabIcon" aria-hidden="true">
              {icon ?? <SaveRoundedIcon fontSize="inherit" />}
            </span>
          }
        >
          <span className="pendingChangesDock__badge" aria-hidden="true" />
        </Fab>
      </span>
      <div className="pendingChangesDock__popup" role="dialog" aria-label={title}>
        <div className="pendingChangesDock__popupHead">
          <span className="pendingChangesDock__popupIcon" aria-hidden="true">
            {icon ?? <SaveRoundedIcon fontSize="inherit" />}
          </span>
          <div className="pendingChangesDock__popupCopy">
            <strong>{title}</strong>
            {summary ? <span>{summary}</span> : null}
          </div>
        </div>
        {actions.length > 0 ? (
          <div className="pendingChangesDock__actions">
            {actions.map((action, index) => (
              <Button
                key={action.key ?? `${action.label}-${index}`}
                variant={action.variant ?? (index === 0 ? 'primary' : 'secondary')}
                size="sm"
                icon={action.icon}
                loading={action.loading}
                disabled={action.disabled}
                globalLoading={action.globalLoading}
                onClick={action.onClick}
              >
                {action.label}
              </Button>
            ))}
          </div>
        ) : null}
        {onDiscard ? (
          <Button
            variant="tertiary"
            tone="danger"
            size="sm"
            onClick={onDiscard}
          >
            {discardLabel}
          </Button>
        ) : null}
      </div>
    </div>
  )
}
